import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { useMenuHubApi } from "../../api/menuHub";
import { Skeleton } from "../skeletons";
import { AdminSolution, SolutionRequest } from "../../types";
import "./styles.scss";

interface SolutionFormModalProps {
    open: boolean;
    mode: "create" | "edit";
    solution?: AdminSolution | null;
    onClose: () => void;
    onSaved: () => void;
}

const SolutionFormModal: React.FC<SolutionFormModalProps> = ({
    open,
    mode,
    solution,
    onClose,
    onSaved,
}) => {
    const api = useMenuHubApi();
    const apiRef = useRef(api);
    apiRef.current = api;

    const [cdEstabelecimento, setCdEstabelecimento] = useState("");
    const [nmTitulo, setNmTitulo] = useState("");
    const [nmPainel, setNmPainel] = useState("");
    const [nmIcone, setNmIcone] = useState("");
    const [href, setHref] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;
        setCdEstabelecimento("");
        setNmTitulo("");
        setNmPainel("");
        setNmIcone("");
        setHref("");
        setError("");

        if (mode !== "edit" || !solution?.id) {
            setLoading(false);
            return;
        }

        setLoading(true);

        const loadSolution = async () => {
            try {
                const details = await apiRef.current.getSolucaoById(solution.id);

                if (!cancelled) {
                    setCdEstabelecimento(details.cdEstabelecimento || "");
                    setNmTitulo(details.titulo || "");
                    setNmPainel(details.nome || "");
                    setNmIcone(details.icone || "");
                    setHref(details.href || "");
                }
            } catch (requestError: unknown) {
                const apiError = requestError as {
                    response?: { data?: { detalhe?: string; mensagem?: string } };
                };

                if (!cancelled) {
                    setError(
                        apiError.response?.data?.detalhe
                        || apiError.response?.data?.mensagem
                        || "Não foi possível carregar os dados da solução.",
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadSolution();

        return () => {
            cancelled = true;
        };
    }, [mode, open, solution?.id]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !saving) {
                onClose();
            }
        };

        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [onClose, open, saving]);

    if (!open) {
        return null;
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const payload: SolutionRequest = {
            cdEstabelecimento: cdEstabelecimento.trim() || undefined,
            nmTitulo: nmTitulo.trim(),
            nmPainel: nmPainel.trim(),
            nmIcone: nmIcone.trim(),
            href: href.trim(),
        };

        const requiredValues = [
            payload.nmTitulo,
            payload.nmPainel,
            payload.nmIcone,
            payload.href,
        ];

        if (requiredValues.some((value) => !value)) {
            setError("Preencha todos os campos da solução.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            if (mode === "edit" && solution?.id) {
                await apiRef.current.updateSolucao(solution.id, payload);
            } else {
                await apiRef.current.createSolucao(payload);
            }

            toast.dismiss();
            toast.success(
                mode === "edit"
                    ? "Solução atualizada com sucesso"
                    : "Solução criada com sucesso",
            );
            onSaved();
            onClose();
        } catch (requestError: unknown) {
            const apiError = requestError as {
                response?: { data?: { detalhe?: string; mensagem?: string } };
            };
            setError(
                apiError.response?.data?.detalhe
                || apiError.response?.data?.mensagem
                || "Não foi possível salvar a solução.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="solution-modal"
            role="presentation"
            onMouseDown={saving ? undefined : onClose}
        >
            <section
                className="solution-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="solution-modal-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header className="solution-modal__header">
                    <h2 id="solution-modal-title">
                        {mode === "create" ? "Cadastrar Nova Solução" : "Editar Solução"}
                    </h2>
                    <button
                        type="button"
                        className="solution-modal__close"
                        aria-label="Fechar"
                        onClick={onClose}
                        disabled={saving}
                    >
                        <X size={18} />
                    </button>
                </header>

                <form className="solution-modal__form" onSubmit={handleSubmit}>
                    <div className="solution-modal__body">
                        {loading ? (
                            <div
                                className="solution-modal__skeleton"
                                aria-busy="true"
                                aria-label="Carregando dados da solução"
                            >
                                {Array.from({ length: 5 }, (_, index) => (
                                    <div key={index}>
                                        <Skeleton height={14} width={150} />
                                        <Skeleton height={48} width="100%" borderRadius={16} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="solution-modal__fields">
                                <label>
                                    <span>Estabelecimento</span>
                                    <select
                                        value={cdEstabelecimento}
                                        autoFocus
                                        onChange={(event) => setCdEstabelecimento(event.target.value)}
                                    >
                                        <option value="" disabled>Selecione o estabelecimento</option>
                                        <option value="2">Hospital São Marcelino Champagnat</option>
                                        <option value="33">Hospital Universitário Cajuru</option>
                                    </select>
                                </label>
                                <label>
                                    <span>Título</span>
                                    <input
                                        type="text"
                                        value={nmTitulo}
                                        placeholder="Título ou agrupador da solução"
                                        required
                                        onChange={(event) => setNmTitulo(event.target.value)}
                                    />
                                </label>
                                <label>
                                    <span>Solução</span>
                                    <input
                                        type="text"
                                        value={nmPainel}
                                        placeholder="Nome da solução"
                                        required
                                        onChange={(event) => setNmPainel(event.target.value)}
                                    />
                                </label>
                                <label>
                                    <span>Ícone</span>
                                    <input
                                        type="text"
                                        value={nmIcone}
                                        placeholder="Classe FontAwesome (ex: fa-solid fa-hospital)"
                                        required
                                        onChange={(event) => setNmIcone(event.target.value)}
                                    />
                                </label>
                                <label>
                                    <span>HREF</span>
                                    <input
                                        type="text"
                                        value={href}
                                        placeholder="URL ou rota da solução"
                                        required
                                        onChange={(event) => setHref(event.target.value)}
                                    />
                                </label>
                            </div>
                        )}

                        {error && <p className="solution-modal__error" role="alert">{error}</p>}
                    </div>

                    <footer className="solution-modal__footer">
                        <button
                            type="button"
                            className="solution-modal__button solution-modal__button--secondary"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="solution-modal__button solution-modal__button--primary"
                            disabled={loading || saving}
                        >
                            {saving ? "Salvando..." : "Salvar"}
                        </button>
                    </footer>
                </form>
            </section>
        </div>
    );
};

export default SolutionFormModal;
